import React from "react";

// The live store listings. Single source of truth - the mobile app page, both footers, and the
// MobileApplication JSON-LD all read from here.
export const PLAY_URL = "https://play.google.com/store/apps/details?id=com.gearcheckauto.app";
export const APP_STORE_URL = "https://apps.apple.com/us/app/gearcheckauto/id6794009877";

// Minimum OS versions, kept next to the links because the same two strings appear in the page
// copy and in the JSON-LD. Android matches the Play listing; iOS matches
// IPHONEOS_DEPLOYMENT_TARGET in frontend/ios/App/App.xcodeproj.
export const ANDROID_MIN_OS = "Android 6.0 and up";
export const IOS_MIN_OS = "iOS 15.0 and up";

// Store links for the native apps.
//
// These are plain styled links rather than Google's "Get it on Google Play" and Apple's
// "Download on the App Store" badge artwork. Using the badges is optional; using them
// *incorrectly* is not allowed, and each has to be downloaded from the vendor (Google's Partner
// Marketing Hub, Apple's Marketing Resources) rather than recreated. If you'd rather ship the
// real badges, drop the official assets at `frontend/public/badges/` and swap the <span> block
// in each button below for:
//
//   <img src="/badges/google-play-badge.png" alt="Get it on Google Play"
//        width={646} height={250} className="h-12 w-auto" />
//
// Google's rules for that path: use the artwork unmodified, render it at least 40px tall, leave
// clear space around it equal to a quarter of its height, and keep the trademark attribution
// line (already rendered on the mobile app page) somewhere on the page. Apple's are similar:
// unmodified artwork, a minimum height, and clear space of at least 10% of the badge height.

const BUTTON_CLASSES =
  "inline-flex items-center gap-2.5 whitespace-nowrap rounded-lg bg-slate-900 px-5 py-3 " +
  "text-base font-semibold text-white shadow-sm hover:bg-slate-800 " +
  "dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 ";

function StoreButton({ href, label, className = "" }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={BUTTON_CLASSES + className}>
      <span className="material-symbols-outlined text-xl leading-none" aria-hidden="true">
        download
      </span>
      {label}
    </a>
  );
}

export function PlayStoreButton({ className = "" }) {
  return <StoreButton href={PLAY_URL} label="Get it on Google Play" className={className} />;
}

export function AppStoreButton({ className = "" }) {
  return <StoreButton href={APP_STORE_URL} label="Download on the App Store" className={className} />;
}

// Both stores together. The apps are the same product on two platforms, so nothing here guesses
// at the visitor's device - showing both is one fewer thing to get wrong, and a desktop visitor
// sending the page to their phone needs whichever one they don't have.
export function StoreButtons({ className = "" }) {
  return (
    <>
      <PlayStoreButton className={className} />
      <AppStoreButton className={className} />
    </>
  );
}

// Compact variants for the two site footers, which sit on a light surface rather than the
// page's dark CTA band.
const FOOTER_LINK_CLASSES =
  "inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-gray-600 " +
  "bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-slate-900 dark:text-gray-100 " +
  "hover:bg-slate-50 dark:hover:bg-gray-700";

function FooterStoreLink({ href, label }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={FOOTER_LINK_CLASSES}>
      <span className="material-symbols-outlined text-lg leading-none" aria-hidden="true">
        download
      </span>
      {label}
    </a>
  );
}

export function FooterStoreLinks() {
  return (
    <div className="mt-4 flex flex-wrap gap-2">
      <FooterStoreLink href={PLAY_URL} label="Get it on Google Play" />
      <FooterStoreLink href={APP_STORE_URL} label="Download on the App Store" />
    </div>
  );
}
